import axios from 'axios'
import Cookies from 'js-cookie'

let isRedirectingToLogin = false

const http = axios.create({
  baseURL: verifyUrl(),
  headers: {
    Accept: 'application/json+pdf',
    'Content-Type': 'application/json+pdf',
  },
})

function handlingError(error) {
  const defaultMessage = 'Erro de conexão com o servidor.'
  const response = error && error.response ? error.response : null
  const data = response && response.data ? response.data : null
  const status = response && response.status ? response.status : null

  let message = defaultMessage
  let errors = null

  if (data) {
    if (data.errors) {
      errors = data.errors
    } else if (data.error) {
      errors = data.error
    }

    // The API sends `message` already resolved to the first validation error,
    // which reads better than every error concatenated. The flattened form is
    // only a fallback for payloads that omit it.
    if (data.message) {
      message = data.message
    } else if (errors) {
      message = formatValidationErrors(errors)
    } else if (typeof data === 'string') {
      message = data
    }
  } else if (error && error.message) {
    message = error.message
  }

  if (status === 503 && !hasApiMessage(data)) {
    message = defaultMessage
  }

  // `errors` carries the per-field validation bag ({ field: [msg] }) so forms
  // can highlight individual inputs; `message` remains the single-line text
  // every current consumer displays. `data` is the optional business payload
  // ApiException attaches to a 4xx (Controller::apiError only emits it when
  // ApiException::getData() is set) — the public cart reads
  // `{ codigo, saldo, quantidade, issues }` from it to mark the offending
  // line; without it the frontend only has a sentence and cannot tell which
  // item failed.
  const handledError = {
    success: false,
    status,
    message,
    errors,
    data: data && data.data ? data.data : null,
  }

  if (isUnauthenticatedError(handledError)) {
    redirectToLogin()
  }

  return handledError
}

function hasApiMessage(data) {
  return !!(data && (data.message || data.error || data.errors))
}

// Downloads (PDF) usam responseType: 'blob', entao o corpo JSON de um erro
// chega como Blob e `data.message` some — a mensagem viraria "Request failed
// with status code 403". Reidrata o corpo antes de montar o erro tratado.
async function parseBlobErrorBody(error) {
  const data = error && error.response ? error.response.data : null

  if (!data || typeof Blob === 'undefined' || !(data instanceof Blob)) {
    return
  }

  if (typeof data.text !== 'function') {
    return
  }

  try {
    const text = await data.text()
    error.response.data = JSON.parse(text)
  } catch (e) {
    // corpo nao-JSON (o proprio PDF, um HTML de erro): mantem como veio
  }
}

function formatValidationErrors(errors) {
  if (!errors) {
    return 'Erro de validação.'
  }

  if (typeof errors === 'string') {
    return errors
  }

  if (Array.isArray(errors)) {
    return errors.join('\n')
  }

  if (typeof errors === 'object') {
    return Object.keys(errors)
      .map((key) => {
        const value = errors[key]

        if (Array.isArray(value)) {
          return value.join('\n')
        }

        if (typeof value === 'object' && value !== null) {
          return Object.values(value).join('\n')
        }

        return value
      })
      .filter(
        (message) => message !== undefined && message !== null && message !== ''
      )
      .join('\n')
  }

  return 'Erro de validação.'
}

function isUnauthenticatedError(error) {
  if (!error || error.status !== 401) {
    return false
  }

  const message = String(error.message || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

  // O backend traduz a mensagem (resources/lang/*/errors.php, chave
  // errors.rules.unauthenticated), entao o texto varia com o locale: pt manda
  // "nao autenticado" e es manda "no autenticado" — que nao e substring do
  // primeiro. Sem a variante espanhola o redirect so funcionaria em portugues.
  return (
    message.includes('unauthenticated') ||
    message.includes('nao autenticado') ||
    message.includes('nao autenticada') ||
    message.includes('no autenticado') ||
    message.includes('no autenticada') ||
    message.includes('not authenticated')
  )
}

function redirectToLogin() {
  if (isRedirectingToLogin) {
    return
  }

  const currentPath = window.location.pathname || ''

  if (currentPath.includes('/login')) {
    return
  }

  isRedirectingToLogin = true
  window.location.href = '/login'
}

function verifyUrl() {
  const hostname = window.location.hostname
  const arrHostname = hostname.split('.')
  return process.env.MIX_API_URL
}

http.interceptors.request.use(function (config) {
  const selectedFilial = Cookies.get('filialCodigo')
  config.headers.filial = selectedFilial || ''
  // Lets the backend answer validation errors in the UI's language. Sent as a
  // header rather than read from the cookie server-side, since EncryptCookies
  // discards cookies written client-side by js-cookie.
  config.headers['X-Locale'] = Cookies.get('locale') || 'pt'
  return config
})

http.interceptors.response.use(
  (response) => {
    return response
  },
  async function (error) {
    await parseBlobErrorBody(error)
    const handledError = handlingError(error)
    return Promise.reject(handledError)
  }
)

export default http
